import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../stores/app';
import { useSocketEvent } from '../lib/socket';

export function useClientTimer() {
  const setTimeLeft = useAppStore((s) => s.setTimeLeft);
  
  const serverTimeDiff = useRef(0);
  const endTime = useRef(0);
  const animationId = useRef<number | null>(null);

  // Калибровка времени
  const calibrate = useCallback((serverTime: number, latencyMs: number = 50) => {
    serverTimeDiff.current = serverTime - Date.now() + latencyMs / 2;
  }, []);

  // Получение оставшегося времени
  const getTimeLeft = useCallback(() => {
    const adjustedNow = Date.now() + serverTimeDiff.current;
    return Math.max(0, endTime.current - adjustedNow);
  }, []);

  // Запуск отображения
  const startDisplay = useCallback(() => {
    const update = () => {
      const timeLeft = getTimeLeft();
      setTimeLeft(timeLeft);
      
      if (timeLeft > 0) {
        animationId.current = requestAnimationFrame(update);
      }
    };
    update();
  }, [getTimeLeft, setTimeLeft]);

  // Остановка
  const stop = useCallback(() => {
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
      animationId.current = null;
    }
  }, []);

  // Обработка событий таймера
  useSocketEvent('timer:start', useCallback((data: { endTime: number; serverTime: number }) => {
    calibrate(data.serverTime);
    endTime.current = data.endTime;
    startDisplay();
  }, [calibrate, startDisplay]));

  useSocketEvent('timer:tick', useCallback((data: { timeLeft: number; serverTime: number }) => {
    calibrate(data.serverTime);
    // Корректируем если сильно разошлись
    const localTimeLeft = getTimeLeft();
    if (Math.abs(localTimeLeft - data.timeLeft) > 500) {
      endTime.current = Date.now() + serverTimeDiff.current + data.timeLeft;
    }
  }, [calibrate, getTimeLeft]));

  useSocketEvent('timer:complete', useCallback(() => {
    stop();
    setTimeLeft(0);
  }, [stop, setTimeLeft]));

  // Очистка при размонтировании
  useEffect(() => {
    return () => stop();
  }, [stop]);

  return { getTimeLeft, stop };
}
